import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, BarChart3, BellRing, Check, ChevronRight, Loader2, Lock, Plus, Send, Trash2, Unlock } from 'lucide-react'
import Field, { inputClass } from '../Field'
import Select from '../Select'
import Modal from '../Modal'
import { Badge } from '../ChartCard'
import { isValidEmail } from '../../lib/utils'
import type { Api } from '../../lib/api'
import type { SurveyDesign, SurveyRecipient, SurveyRound, SurveyType } from '../../types'

const STATUS: Record<SurveyRound['status'], [string, string]> = {
  draft: ['Draft', 'bg-slate-100 text-slate-600'],
  open: ['Open', 'bg-[#00ACED]/15 text-[#0077A8]'],
  closed: ['Closed', 'bg-slate-100 text-slate-500'],
}

const fmtDay = (iso?: string | null) =>
  iso ? new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : ''

const total = (counts: Record<string, number>) => Object.values(counts).reduce((a, b) => a + b, 0)

const designOf = (r: SurveyDesign): SurveyDesign => ({
  partners: r.partners, departments: r.departments, questions: r.questions,
  emailSubject: r.emailSubject, emailBody: r.emailBody, partnerFallback: r.partnerFallback,
})

const secondary = 'bg-white border border-slate-200 text-slate-700 px-3.5 py-2 rounded-lg text-sm font-semibold hover:bg-slate-50 flex items-center gap-2 shadow-sm disabled:opacity-50'
const primary = 'btn-primary text-white px-4 py-2 rounded-lg text-sm font-semibold flex items-center gap-2 disabled:opacity-50'

/** The round after the newest one, when rounds are named by half or quarter:
 *  "H2 2026" -> "H1 2027", "Q3 2026" -> "Q4 2026". Blank otherwise. */
export function nextRoundName(rounds: SurveyRound[]): string {
  const newest = [...rounds].sort((a, b) => (b.createdAt ?? '').localeCompare(a.createdAt ?? ''))[0]
  const m = newest?.name.trim().match(/^([HQ])([1-4]) (\d{4})$/i)
  if (!m) return ''
  const last = m[1].toUpperCase() === 'H' ? 2 : 4
  const wraps = Number(m[2]) >= last
  return `${m[1].toUpperCase()}${wraps ? 1 : Number(m[2]) + 1} ${wraps ? Number(m[3]) + 1 : m[3]}`
}

export function RoundsPanel({ api, type, rounds, onChanged }: {
  api: Api
  type: SurveyType
  rounds: SurveyRound[]
  onChanged: (rounds: SurveyRound[]) => void
}) {
  const [openId, setOpenId] = useState<string | null>(null)
  const open = rounds.find(r => r.id === openId)
  const replace = (round: SurveyRound) => onChanged(rounds.map(r => (r.id === round.id ? round : r)))

  if (open) {
    return <RoundView key={open.id} api={api} round={open} onBack={() => setOpenId(null)} onSaved={replace}
      onDeleted={() => { onChanged(rounds.filter(r => r.id !== open.id)); setOpenId(null) }} />
  }
  return <RoundList api={api} type={type} rounds={rounds} onOpen={setOpenId}
    onCreated={round => { onChanged([round, ...rounds]); setOpenId(round.id) }} />
}

function RoundList({ api, type, rounds, onOpen, onCreated }: {
  api: Api
  type: SurveyType
  rounds: SurveyRound[]
  onOpen: (id: string) => void
  onCreated: (round: SurveyRound) => void
}) {
  const lastSent = [...rounds].filter(r => r.sentAt).sort((a, b) => (b.sentAt ?? '').localeCompare(a.sentAt ?? ''))[0]
  const [creating, setCreating] = useState(false)
  const [name, setName] = useState('')
  const [from, setFrom] = useState('')
  const [error, setError] = useState('')

  const start = () => { setName(nextRoundName(rounds)); setFrom(lastSent?.id ?? ''); setError(''); setCreating(true) }
  const create = async () => {
    try {
      onCreated(await api.createSurveyRound({ typeId: type.id, name: name.trim(), recipientsFrom: from || undefined }))
    } catch (e: any) {
      setError(e?.message || 'The round could not be created.')
    }
  }

  return (
    <div className="card p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="font-bold text-slate-800">Rounds</h2>
          <p className="text-sm text-slate-500">Each round is one sending.</p>
        </div>
        {!creating && <button type="button" className={primary} onClick={start}><Plus className="w-4 h-4" /> New round</button>}
      </div>

      {creating && (
        <div className="flex items-end gap-3 mb-4 p-4 bg-slate-50 border border-slate-200 rounded-xl flex-wrap">
          <Field label="Name" className="w-48">
            <input autoFocus className={inputClass} value={name} onChange={e => setName(e.target.value)} placeholder="H1 2027"
              onKeyDown={e => { if (e.key === 'Enter' && name.trim()) create() }} />
          </Field>
          <Field label="Start with the people from" className="w-64">
            <Select value={from} onChange={setFrom} options={[['', 'Nobody'] as const, ...rounds.map(r => [r.id, r.name] as const)]} />
          </Field>
          <button type="button" className={primary} disabled={!name.trim()} onClick={create}>Create round</button>
          <button type="button" className="text-sm text-slate-500 hover:text-slate-700 px-2 py-2" onClick={() => setCreating(false)}>Cancel</button>
          {error && <span className="text-sm text-[#EF3340] w-full">{error}</span>}
        </div>
      )}

      {rounds.length === 0 ? (
        <div className="text-center py-8 text-sm text-slate-400">No rounds yet.</div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-slate-100">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 text-[10px] uppercase tracking-wide text-slate-500">
                <th className="text-left font-semibold px-3 py-2">Round</th>
                <th className="text-left font-semibold px-3 py-2">Status</th>
                <th className="text-left font-semibold px-3 py-2">Sent</th>
                <th className="text-left font-semibold px-3 py-2 w-72">Answered</th>
                <th className="w-10" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rounds.map(r => {
                const invited = total(r.invited)
                const responded = total(r.responded)
                const pct = invited ? Math.round((responded / invited) * 100) : 0
                return (
                  <tr key={r.id} onClick={() => onOpen(r.id)} className="cursor-pointer hover:bg-slate-50/80 group">
                    <td className="px-3 py-3 font-semibold text-slate-800">{r.name}</td>
                    <td className="px-3 py-3"><Badge cls={STATUS[r.status][1]}>{STATUS[r.status][0]}</Badge></td>
                    <td className="px-3 py-3 text-slate-500">{r.sentAt ? fmtDay(r.sentAt) : <span className="text-slate-300">-</span>}</td>
                    <td className="px-3 py-3">
                      {r.status === 'draft' ? (
                        <span className="text-xs text-slate-500">{r.recipients.length ? `Ready to send to ${r.recipients.length}` : 'Add people to send it to'}</span>
                      ) : (
                        <div className="flex items-center gap-3">
                          <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                            <div className="h-full bg-[#005EB8] rounded-full" style={{ width: `${pct}%` }} />
                          </div>
                          <span className="text-xs text-slate-600 tabular-nums whitespace-nowrap">{responded} of {invited} · {pct}%</span>
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-3 text-right"><ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-slate-500 inline" /></td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

type Confirm = { title: string; message: string; label: string; danger?: boolean; run: () => Promise<void> } | null

/** One line pasted from Excel: a name, an email and a department, in any
 *  order, split by tabs, commas or semicolons, and "Board" for a Board
 *  member (unless the survey has a department of that name). */
export function parsePeople(text: string, design: SurveyDesign, fallback: string, allBoard = false): { people: SurveyRecipient[]; problems: string[] } {
  const people: SurveyRecipient[] = []
  const problems: string[] = []
  const boardWord = (p: string) => p.toLowerCase() === 'board' && !design.departments.some(d => d.name.toLowerCase() === 'board')
  for (const line of text.split(/\r?\n/).map(l => l.trim()).filter(Boolean)) {
    const all = line.split(/\t|;|,/).map(p => p.trim()).filter(Boolean)
    const parts = all.filter(p => !boardWord(p))
    const email = parts.find(p => p.includes('@')) ?? ''
    const department = design.departments.find(d => parts.some(p => p.toLowerCase() === d.name.toLowerCase()))
    const name = parts.find(p => p !== email && p.toLowerCase() !== department?.name.toLowerCase()) ?? ''
    if (!isValidEmail(email)) { problems.push(`${line}: no valid email`); continue }
    // "All into ..." chosen beside the paste wins over a department in the line.
    const departmentId = fallback || department?.id
    if (!departmentId) { problems.push(`${email}: department not recognised`); continue }
    people.push({ name, email, departmentId, board: allBoard || all.length !== parts.length, invitedAt: null })
  }
  return { people, problems }
}

/** Which of the pasted people to add: not anyone already on the list, nor a
 *  second line with the same email in the paste itself. */
export function newPeople(existing: SurveyRecipient[], parsed: SurveyRecipient[]): { added: SurveyRecipient[]; duplicates: string[] } {
  const known = new Set(existing.map(p => p.email.toLowerCase()))
  const added: SurveyRecipient[] = []
  const duplicates: string[] = []
  for (const p of parsed) {
    const email = p.email.toLowerCase()
    if (known.has(email)) duplicates.push(`${p.email}: already on the list`)
    else { known.add(email); added.push(p) }
  }
  return { added, duplicates }
}

/** One person typed in, or why they can't be added. */
export function onePerson(
  existing: SurveyRecipient[],
  entry: { name: string; email: string; departmentId: string; board: boolean },
): { person: SurveyRecipient } | { problem: string } {
  const email = entry.email.trim()
  if (!isValidEmail(email)) return { problem: 'Enter a valid email.' }
  if (!entry.departmentId) return { problem: 'Choose their department.' }
  if (existing.some(p => p.email.toLowerCase() === email.toLowerCase())) return { problem: `${email} is already on the list.` }
  return { person: { name: entry.name.trim(), email, departmentId: entry.departmentId, board: entry.board, invitedAt: null } }
}

function Steps({ round, people }: { round: SurveyRound; people: number }) {
  const invited = total(round.invited)
  const responded = total(round.responded)
  const steps = [
    { label: 'Add people', detail: `${people} ${people === 1 ? 'person' : 'people'}`, done: people > 0 && !!round.sentAt },
    { label: 'Send', detail: round.sentAt ? fmtDay(round.sentAt) : 'Not sent yet', done: !!round.sentAt },
    { label: 'Collect answers', detail: round.sentAt ? `${responded} of ${invited} answered` : '', done: round.status === 'closed' },
    { label: 'Close', detail: round.closedAt ? fmtDay(round.closedAt) : '', done: round.status === 'closed' },
  ]
  const current = steps.findIndex(s => !s.done)
  return (
    <ol className="flex items-start mb-5">
      {steps.map((s, i) => (
        <li key={s.label} className="flex-1 flex items-start">
          <div className="flex flex-col items-center text-center flex-1 min-w-0">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${s.done
              ? 'bg-[#00843D] text-white' : i === current ? 'bg-[#005EB8] text-white ring-4 ring-[#005EB8]/15' : 'bg-slate-100 text-slate-400'}`}>
              {s.done ? <Check className="w-4 h-4" /> : i + 1}
            </div>
            <div className={`text-xs font-semibold mt-1.5 ${i === current ? 'text-slate-800' : s.done ? 'text-slate-600' : 'text-slate-400'}`}>{s.label}</div>
            <div className="text-[11px] text-slate-400 truncate max-w-full">{s.detail || ' '}</div>
          </div>
          {i < steps.length - 1 && <div className={`h-0.5 flex-1 mt-4 -mx-6 ${s.done ? 'bg-[#00843D]' : 'bg-slate-200'}`} />}
        </li>
      ))}
    </ol>
  )
}

function RoundView({ api, round, onBack, onSaved, onDeleted }: {
  api: Api
  round: SurveyRound
  onBack: () => void
  onSaved: (r: SurveyRound) => void
  onDeleted: () => void
}) {
  const navigate = useNavigate()
  const [name, setName] = useState(round.name)
  const [people, setPeople] = useState<SurveyRecipient[]>(round.recipients)
  const [paste, setPaste] = useState('')
  const [pasteDepartment, setPasteDepartment] = useState('')
  const [pasteBoard, setPasteBoard] = useState(false)
  const [mode, setMode] = useState<'one' | 'paste'>('one')
  const [one, setOne] = useState({ name: '', email: '', departmentId: '', board: false })
  const [problems, setProblems] = useState<string[]>([])
  const [busy, setBusy] = useState(false)
  const [notice, setNotice] = useState<{ ok: boolean; text: string } | null>(null)
  const [confirm, setConfirm] = useState<Confirm>(null)

  useEffect(() => { setName(round.name); setPeople(round.recipients) }, [round])

  const closed = round.status === 'closed'
  const dirty = name !== round.name || JSON.stringify(people) !== JSON.stringify(round.recipients)
  const pending = people.filter(p => !p.invitedAt).length
  const invited = total(round.invited)
  const responded = total(round.responded)
  const departments = round.departments
  const departmentName = (id: string) => departments.find(d => d.id === id)?.name ?? '-'
  const results = () => navigate(`/survey-results?survey=${encodeURIComponent(round.typeId)}&round=${encodeURIComponent(round.id)}`)

  const run = async (work: () => Promise<void>) => {
    setBusy(true)
    setNotice(null)
    try { await work() } catch (e: any) { setNotice({ ok: false, text: e?.message || 'That did not work.' }) } finally { setBusy(false) }
  }

  const save = async () => {
    const saved = await api.updateSurveyRound(round.id, { ...designOf(round), name: name.trim(), recipients: people })
    onSaved(saved)
  }

  const askSend = () => setConfirm({
    title: round.status === 'draft' ? `Send ${name.trim() || round.name}?` : 'Send to the people added?',
    message: `${pending} ${pending === 1 ? 'person gets' : 'people get'} the invitation email with their own link.${round.status === 'draft' ? " The questions can't change once it's sent." : ''}`,
    label: 'Send',
    run: async () => {
      if (dirty) await save()
      const result = await api.sendSurveyRound(round.id, window.location.origin)
      onSaved(result.round)
      setNotice(result.failed.length
        ? { ok: false, text: `Sent to ${result.sent.length}. Not sent to ${result.failed.join(', ')} - send again to retry.` }
        : { ok: true, text: `Sent to ${result.sent.length} ${result.sent.length === 1 ? 'person' : 'people'}.` })
    },
  })

  const askRemind = () => setConfirm({
    title: 'Send a reminder?',
    message: `Everyone sent a link (${invited}) gets it again. Who has answered isn't recorded, so the reminder thanks those who have.`,
    label: 'Send reminder',
    run: async () => {
      // Unsaved changes to the list are saved first, so a person just removed
      // isn't reminded and the edits aren't lost.
      if (dirty) await save()
      const result = await api.remindSurveyRound(round.id, window.location.origin)
      onSaved(result.round)
      setNotice({ ok: !result.failed.length, text: `Reminder sent to ${result.sent.length}.${result.failed.length ? ` Not sent to ${result.failed.join(', ')}.` : ''}` })
    },
  })

  const askClose = () => setConfirm({
    title: 'Close this round?', message: 'Links stop taking answers. You can reopen it.',
    label: 'Close round',
    run: async () => {
      if (dirty) await save()
      onSaved(await api.closeSurveyRound(round.id))
    },
  })

  const add = () => {
    const { people: parsed, problems: found } = parsePeople(paste, round, pasteDepartment, pasteBoard)
    const { added, duplicates } = newPeople(people, parsed)
    setPeople([...people, ...added])
    setProblems([...found, ...duplicates])
    if (!found.length) setPaste('')
  }

  const addOne = () => {
    const result = onePerson(people, one)
    if ('problem' in result) { setProblems([result.problem]); return }
    setPeople([...people, result.person])
    setProblems([])
    // The department stays chosen, for the next person from it.
    setOne({ ...one, name: '', email: '', board: false })
  }

  // What to do next, with the button for it.
  const next = (() => {
    if (round.status === 'draft' && !people.length) return { text: 'Add the people to send it to', sub: 'Add them below, one by one or pasted from Excel.', actions: null }
    if (round.status === 'draft') return {
      text: `Ready to send to ${pending} ${pending === 1 ? 'person' : 'people'}`,
      sub: 'Each gets the invitation email with their own link.',
      actions: <button type="button" className={primary} disabled={busy} onClick={askSend}>{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send to {pending}</button>,
    }
    if (round.status === 'open') return {
      text: `${responded} of ${invited} have answered`,
      sub: round.reminders.length ? `Reminded ${round.reminders.map(r => fmtDay(r.at)).join(', ')}` : 'No reminder sent yet',
      actions: (
        <>
          {pending > 0 && <button type="button" className={primary} disabled={busy} onClick={askSend}><Send className="w-4 h-4" /> Send to {pending} added</button>}
          <button type="button" className={secondary} disabled={busy} onClick={askRemind}><BellRing className="w-4 h-4" /> Send reminder</button>
          <button type="button" className={secondary} disabled={busy} onClick={askClose}><Lock className="w-4 h-4" /> Close round</button>
          <button type="button" className={secondary} onClick={results}><BarChart3 className="w-4 h-4" /> Results</button>
        </>
      ),
    }
    return {
      text: `Closed ${fmtDay(round.closedAt)}`,
      sub: `${responded} of ${invited} answered`,
      actions: (
        <>
          <button type="button" className={secondary} disabled={busy} onClick={() => run(async () => onSaved(await api.reopenSurveyRound(round.id)))}><Unlock className="w-4 h-4" /> Reopen</button>
          <button type="button" className={primary} onClick={results}><BarChart3 className="w-4 h-4" /> View results</button>
        </>
      ),
    }
  })()

  const byDepartment = departments.map(d => ({
    d, people: people.filter(p => p.departmentId === d.id).length, answered: round.responded[d.id] ?? 0,
  })).filter(x => x.people > 0)
  const onBoard = people.filter(p => p.board).length
  const boardAnswered = total(round.respondedBoard ?? {})

  return (
    <div className="space-y-4">
      <Modal open={!!confirm} title={confirm?.title ?? ''} message={confirm?.message ?? ''} confirmLabel={confirm?.label}
        cancelLabel="Cancel" variant={confirm?.danger ? 'danger' : 'info'}
        onCancel={() => setConfirm(null)}
        onConfirm={() => { const c = confirm; setConfirm(null); if (c) run(c.run) }} />

      <button type="button" onClick={onBack} className="flex items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-slate-800">
        <ArrowLeft className="w-4 h-4" /> All rounds
      </button>

      <div className="card p-6">
        <div className="flex items-center justify-between gap-4 mb-5">
          <div className="flex items-center gap-3">
            <input className="text-xl font-bold text-slate-800 bg-transparent border-b border-transparent hover:border-slate-200 focus:border-[#00ACED] focus:outline-none w-56"
              value={name} onChange={e => setName(e.target.value)} disabled={closed} aria-label="Round name" />
            <Badge cls={STATUS[round.status][1]}>{STATUS[round.status][0]}</Badge>
          </div>
          <div className="flex items-center gap-2">
            {!closed && dirty && (
              <button type="button" className={secondary} disabled={busy} onClick={() => run(async () => { await save(); setNotice({ ok: true, text: 'Saved.' }) })}>Save changes</button>
            )}
            {round.status === 'draft' && (
              <button type="button" className="p-2 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100" disabled={busy} aria-label="Delete round"
                onClick={() => setConfirm({
                  title: 'Delete this round?', message: `${round.name} hasn't been sent, so only its list of people is lost.`,
                  label: 'Delete', danger: true, run: async () => { await api.deleteSurveyRound(round.id); onDeleted() },
                })}><Trash2 className="w-4 h-4" /></button>
            )}
          </div>
        </div>

        <Steps round={round} people={people.length} />

        <div className="flex items-center justify-between gap-4 rounded-xl bg-[#005EB8]/5 border border-[#005EB8]/15 px-4 py-3">
          <div>
            <div className="text-sm font-bold text-slate-800">{next.text}</div>
            <div className="text-xs text-slate-500">{next.sub}</div>
          </div>
          {next.actions && <div className="flex items-center gap-2 flex-shrink-0">{next.actions}</div>}
        </div>

        {notice && (
          <div className={`mt-3 px-3 py-2 rounded-lg text-sm border ${notice.ok ? 'bg-green-50 border-green-200 text-green-700' : 'bg-red-50 border-red-200 text-red-700'}`}>{notice.text}</div>
        )}
      </div>

      <div className="card p-6">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-bold text-slate-800">People <span className="text-slate-400 font-semibold">· {people.length}</span></h3>
          <span className="text-[11px] text-slate-400">Who has answered isn't recorded</span>
        </div>

        {byDepartment.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-4">
            {byDepartment.map(({ d, people: n, answered }) => (
              <span key={d.id} className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-slate-100 text-slate-600 rounded-lg text-xs font-medium">
                {d.name}
                <span className="font-bold text-slate-800 tabular-nums">{n}</span>
                {round.status !== 'draft' && <span className="text-slate-400 tabular-nums">· {answered} answered</span>}
              </span>
            ))}
            {onBoard > 0 && (
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-[#002677]/10 text-[#002677] rounded-lg text-xs font-medium">
                Board, across departments
                <span className="font-bold tabular-nums">{onBoard}</span>
                {round.status !== 'draft' && <span className="opacity-60 tabular-nums">· {boardAnswered} answered</span>}
              </span>
            )}
          </div>
        )}

        {!closed && (
          <div className="mb-4 p-4 bg-slate-50 border border-slate-200 rounded-xl">
            <div className="flex items-center justify-between gap-3 mb-3">
              <span className="text-xs font-semibold text-slate-500 uppercase">Add people</span>
              <div className="flex items-center rounded-lg border border-slate-200 overflow-hidden text-xs font-semibold bg-white">
                {([['one', 'One person'], ['paste', 'Paste from Excel']] as const).map(([value, label]) => (
                  <button key={value} type="button" aria-pressed={mode === value} onClick={() => { setMode(value); setProblems([]) }}
                    className={`px-3 py-1.5 transition-colors ${mode === value ? 'bg-[#005EB8] text-white' : 'text-slate-500 hover:bg-slate-50'}`}>
                    {label}
                  </button>
                ))}
              </div>
            </div>
            {mode === 'one' ? (
              <form noValidate className="grid grid-cols-[1fr_1.4fr_14rem_auto_auto] gap-2 items-center" onSubmit={e => { e.preventDefault(); addOne() }}>
                <input className={`${inputClass} bg-white`} placeholder="Name" aria-label="Name" value={one.name}
                  onChange={e => setOne({ ...one, name: e.target.value })} />
                <input className={`${inputClass} bg-white`} type="email" placeholder="Email" aria-label="Email" value={one.email}
                  onChange={e => setOne({ ...one, email: e.target.value })} />
                <Select value={one.departmentId} onChange={departmentId => setOne({ ...one, departmentId })} placeholder="Department"
                  options={departments.map(d => [d.id, d.name] as const)} />
                <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer select-none px-1">
                  <input type="checkbox" checked={one.board} onChange={e => setOne({ ...one, board: e.target.checked })} className="rounded border-slate-300 text-[#005EB8] focus:ring-[#005EB8]/30" />
                  Board
                </label>
                <button type="submit" className={secondary} disabled={!one.email.trim()}><Plus className="w-4 h-4" /> Add</button>
              </form>
            ) : (
              <div className="grid grid-cols-[1fr_auto] gap-3 items-start">
                <div>
                  <textarea className={`${inputClass} font-mono text-xs bg-white`} rows={3} value={paste} onChange={e => setPaste(e.target.value)}
                    aria-label="People pasted from Excel" placeholder={'Jane Smith\tjane.smith@danone.com\tFinance'} />
                  <p className="text-[11px] text-slate-400 mt-1">A name, an email and a department per line, in any order. Add Board to a line for a Board member.</p>
                </div>
                <div className="flex flex-col gap-2 w-56">
                  <Select value={pasteDepartment} onChange={setPasteDepartment}
                    options={[['', 'Department from the paste'] as const, ...departments.map(d => [d.id, `All into ${d.name}`] as const)]} />
                  <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer select-none px-1">
                    <input type="checkbox" checked={pasteBoard} onChange={e => setPasteBoard(e.target.checked)} className="rounded border-slate-300 text-[#005EB8] focus:ring-[#005EB8]/30" />
                    Board members
                  </label>
                  <button type="button" className={secondary} disabled={!paste.trim()} onClick={add}><Plus className="w-4 h-4" /> Add to list</button>
                </div>
              </div>
            )}
            {problems.length > 0 && (
              <ul className="mt-2 text-xs text-[#EF3340] space-y-0.5">{problems.map(p => <li key={p}>{p}</li>)}</ul>
            )}
          </div>
        )}

        <div className="overflow-hidden rounded-lg border border-slate-100 max-h-[420px] overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0">
              <tr className="bg-slate-50 text-[10px] uppercase tracking-wide text-slate-500">
                <th className="text-left font-semibold px-3 py-2">Name</th>
                <th className="text-left font-semibold px-3 py-2">Email</th>
                <th className="text-left font-semibold px-3 py-2 w-56">Department</th>
                <th className="text-center font-semibold px-3 py-2 w-20">Board</th>
                <th className="text-left font-semibold px-3 py-2">Link sent</th>
                <th className="w-10" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {people.map((p, i) => (
                <tr key={p.email}>
                  <td className="px-3 py-1.5 text-slate-700">{p.name || <span className="text-slate-300">-</span>}</td>
                  <td className="px-3 py-1.5 text-slate-600">{p.email}</td>
                  <td className="px-3 py-1.5">
                    {closed ? departmentName(p.departmentId) : (
                      <select value={p.departmentId} aria-label={`Department of ${p.email}`}
                        onChange={e => setPeople(people.map((x, j) => (j === i ? { ...x, departmentId: e.target.value } : x)))}
                        className="w-full text-xs border border-slate-200 rounded-md px-2 py-1 text-slate-700 bg-white focus:outline-none focus:border-danone-cyan cursor-pointer">
                        {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                      </select>
                    )}
                  </td>
                  <td className="px-3 py-1.5 text-center">
                    <input type="checkbox" checked={!!p.board} disabled={closed} aria-label={`${p.email} is on the Board`}
                      onChange={e => setPeople(people.map((x, j) => (j === i ? { ...x, board: e.target.checked } : x)))}
                      className="rounded border-slate-300 text-[#005EB8] focus:ring-[#005EB8]/30 cursor-pointer disabled:cursor-default" />
                  </td>
                  <td className="px-3 py-1.5 text-xs text-slate-500">{p.invitedAt ? fmtDay(p.invitedAt) : <span className="text-[#F88806] font-semibold">Not yet</span>}</td>
                  <td className="px-2 py-1.5">
                    {!closed && (
                      <button type="button" className="p-1.5 rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-100"
                        onClick={() => setPeople(people.filter((_, j) => j !== i))} aria-label={`Remove ${p.email}`}><Trash2 className="w-4 h-4" /></button>
                    )}
                  </td>
                </tr>
              ))}
              {people.length === 0 && <tr><td colSpan={6} className="px-3 py-6 text-center text-xs text-slate-400">Nobody yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
